import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, LOGO_URL, apiError, inr, getJoinRef } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import { jobPhotoSrc } from "@/lib/jobPhotos";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RazorpayButton } from "@/components/RazorpayButton";
import { WalletCard } from "@/components/WalletCard";
import { AddressCard } from "@/components/AddressCard";
import { ReferrerBanner } from "@/components/ReferrerBanner";
import { PriceTag } from "@/components/PriceTag";
import { Briefcase, MapPin, Building2, LogOut, ArrowLeft, Upload, Loader2 } from "lucide-react";

const TOKEN_KEY = "candidate_token";
const STATUS_CLS = { applied: "border-blue-400/40 text-blue-300", shortlisted: "border-amber-400/40 text-amber-300", selected: "border-emerald-400/40 text-emerald-300", rejected: "border-destructive/40 text-destructive" };
const cApi = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

function AuthCard({ onDone, refCode }) {
  const [mode, setMode] = useState("signup");
  const [f, setF] = useState({ name: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = mode === "signup" ? { ...f, ref: refCode } : { phone: f.phone, password: f.password };
      const { data } = await axios.post(`${API}/candidate/${mode}`, body);
      localStorage.setItem(TOKEN_KEY, data.token); onDone(data.candidate);
      toast.success(mode === "signup" ? "Account created" : "Signed in");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-card/50 border-gold/30" data-testid="candidate-auth-card">
      {mode === "signup" && <div className="mb-3"><ReferrerBanner code={refCode} /></div>}
      <div className="flex gap-2 mb-4">
        {[["signup", "Sign up"], ["login", "Sign in"]].map(([k, l]) => (
          <Button key={k} type="button" size="sm" data-testid={`candidate-${k}-tab`} onClick={() => setMode(k)}
            className={mode === k ? "flex-1 bg-gold text-[#0a2e22] hover:bg-gold/90" : "flex-1 bg-transparent border border-gold/40 text-gold hover:bg-gold/10"}>{l}</Button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3">
        {mode === "signup" && <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="candidate-name-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 bg-card/60" /></div>}
        <div><Label className="text-xs text-muted-foreground">Mobile number</Label><Input data-testid="candidate-phone-input" required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+9198XXXXXXXX" /></div>
        <div><Label className="text-xs text-muted-foreground">Password</Label><Input data-testid="candidate-password-input" type="password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1 bg-card/60" /></div>
        <Button type="submit" data-testid="candidate-auth-submit" disabled={busy} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}</Button>
      </form>
    </Card>
  );
}

function ApplyDialog({ job, onClose, onApplied, refCode }) {
  const [cv, setCv] = useState(null);
  const [pp, setPp] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!cv || !pp) { toast.error("Please attach your CV and passport copy"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id); fd.append("note", note); fd.append("ref", refCode || "");
      fd.append("cv", cv); fd.append("passport", pp);
      await cApi().post("/candidate/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted"); onApplied();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const FileField = ({ label, file, set, tid }) => (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="mt-1 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-gold/50 text-gold text-xs cursor-pointer hover:bg-gold/10">
        <Upload className="w-3.5 h-3.5" /> {file ? file.name : "Choose photo or PDF"}
      </span>
      <input data-testid={tid} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => set(e.target.files?.[0] || null)} />
    </label>
  );
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#05261D] border-gold/20">
        <DialogHeader>
          <DialogTitle className="font-serif text-gold">Apply: {job?.post}</DialogTitle>
          <DialogDescription>{job?.company} · {job?.country}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FileField label="CV / Resume" file={cv} set={setCv} tid="apply-cv-input" />
          <FileField label="Passport copy" file={pp} set={setPp} tid="apply-passport-input" />
          <div><Label className="text-xs text-muted-foreground">Note (optional)</Label><Textarea data-testid="apply-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-card/60 min-h-[60px]" placeholder="Experience, age, current location…" /></div>
        </div>
        <DialogFooter><Button data-testid="apply-submit-btn" onClick={submit} disabled={busy} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Application</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function JobsPortal() {
  const [params] = useSearchParams();
  const refCode = params.get("ref") || getJoinRef() || localStorage.getItem("job_ref") || "";
  const focusJob = params.get("job");
  const [jobs, setJobs] = useState([]);
  const [me, setMe] = useState(null);
  const [apps, setApps] = useState([]);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => { if (params.get("ref")) localStorage.setItem("job_ref", params.get("ref")); }, [params]);
  const loadApps = useCallback(() => cApi().get("/candidate/applications").then((r) => setApps(r.data)).catch(() => {}), []);
  useEffect(() => {
    axios.get(`${API}/public/jobs`).then((r) => setJobs(r.data)).catch(() => {});
    if (localStorage.getItem(TOKEN_KEY)) cApi().get("/candidate/me").then((r) => { setMe(r.data); loadApps(); }).catch(() => localStorage.removeItem(TOKEN_KEY));
  }, [loadApps]);
  useEffect(() => { if (focusJob && jobs.length) document.getElementById(`job-${focusJob}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focusJob, jobs]);

  const applied = new Set(apps.map((a) => a.job_id));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setMe(null); setApps([]); };
  const startApply = (j) => {
    if (!me) { toast.info("Please sign up or sign in first"); document.getElementById("candidate-auth")?.scrollIntoView({ behavior: "smooth" }); return; }
    setApplyJob(j);
  };

  return (
    <div className="relative min-h-screen" data-testid="jobs-portal">
      <PageBackground />
      <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border/60 glass sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-3" data-testid="jobs-home-link">
          <ArrowLeft className="w-4 h-4 text-gold" />
          <img src={LOGO_URL} alt="Bengal Umrah House" className="w-9 h-9 rounded-full ring-1 ring-gold/50" />
          <div><p className="font-serif text-sm font-semibold">Bengal Umrah House</p><p className="eyebrow text-gold/80">Jobs Abroad</p></div>
        </Link>
        {me && <Button size="sm" variant="outline" data-testid="candidate-logout-btn" onClick={logout} className="border-gold/40 text-gold gap-1.5 text-xs"><LogOut className="w-3.5 h-3.5" /> {me.name.split(" ")[0]}</Button>}
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <p className="eyebrow text-gold mb-2">Employment</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-white mb-6">Overseas job openings</h1>
          <div className="grid sm:grid-cols-2 gap-4">
            {jobs.length === 0 && <p className="text-sm text-muted-foreground">No openings right now. Please check back soon.</p>}
            {jobs.map((j) => (
              <Card key={j.id} id={`job-${j.id}`} data-testid={`portal-job-${j.id}`} className={`overflow-hidden bg-card/40 flex flex-col ${focusJob === j.id ? "border-gold ring-2 ring-gold/40" : "border-border/60"}`}>
                <img src={jobPhotoSrc(j.photo)} alt={j.post} className="h-40 w-full object-cover" />
                <div className="p-4 flex flex-col flex-1">
                  <p className="font-serif font-semibold text-white text-lg">{j.post}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3 text-gold" /> {j.company}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> {j.country}</p>
                  {j.salary && <p className="text-xs text-emerald-200 mt-1">Salary: {j.salary}</p>}
                  {j.description && <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{j.description}</p>}
                  <div className="mt-auto pt-3 border-t border-border/50 flex items-center justify-between">
                    <div><p className="text-[10px] text-muted-foreground uppercase">Service charge</p><p className="text-gold font-mono"><PriceTag price={j.service_charge} mrp={j.service_charge_mrp} /></p></div>
                    {applied.has(j.id)
                      ? <Badge variant="outline" className="border-emerald-400/40 text-emerald-300">Applied</Badge>
                      : <Button size="sm" data-testid={`apply-job-${j.id}`} onClick={() => startApply(j)} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-xs gap-1"><Briefcase className="w-3.5 h-3.5" /> Apply</Button>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
        <div id="candidate-auth" className="space-y-4 lg:sticky lg:top-20 self-start">
          {!me ? <AuthCard onDone={(c) => { setMe(c); loadApps(); }} refCode={refCode} /> : (
            <Card className="p-5 bg-card/50 border-gold/30" data-testid="candidate-applications">
              <p className="font-serif text-white font-semibold">Assalamu Alaikum, {me.name}</p>
              <p className="text-xs text-muted-foreground mb-3">Your applications</p>
              <WalletCard client={cApi()} token={localStorage.getItem(TOKEN_KEY)} product="job" />
              <div className="mt-4"><AddressCard client={cApi()} /></div>
              {apps.length === 0 && <p className="text-xs text-muted-foreground">You haven't applied yet. Pick a job and tap Apply.</p>}
              {apps.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 py-2 border-t border-border/40">
                  <div className="min-w-0"><p className="text-sm text-white truncate">{a.job_post}</p><p className="text-[10px] text-muted-foreground">{a.job_company} · {a.job_country}</p></div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.service_charge > 0 && (a.service_paid
                      ? <Badge variant="outline" data-testid={`my-app-paid-${a.id}`} className="border-emerald-400/40 text-emerald-300">Charge paid</Badge>
                      : <RazorpayButton token={localStorage.getItem(TOKEN_KEY)} body={{ kind: "job", application_id: a.id }} label={`Pay ${inr(a.service_charge - (a.wallet_discount || 0))}${a.wallet_discount ? ` (wallet −${inr(a.wallet_discount)})` : ""}`} testid={`my-app-pay-${a.id}`} className="h-7 text-xs px-2" onPaid={loadApps} />)}
                    <Badge variant="outline" data-testid={`my-app-status-${a.id}`} className={`capitalize ${STATUS_CLS[a.status]}`}>{a.status}</Badge>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
      <ApplyDialog job={applyJob} refCode={refCode} onClose={() => setApplyJob(null)} onApplied={() => { setApplyJob(null); loadApps(); }} />
    </div>
  );
}
